<template>
  <div class="studyplan">
    <template v-if="isLoading">
      <i class="fa fa-spinner fa-pulse fa-2x fa-fw" />
    </template>
    <template v-else>
      <div class="studyplan__header">
        <div class="studyplan__header__title">
          <h3>
            {{ trans('studyplan.studyplan') }} {{ activeYear }} - {{ parseInt(activeYear) + 1 }} ({{ getEcts }} {{ trans('core.ects') }})
          </h3>
        </div>
        <div class="studyplan__header__approve">
          <select
            v-if="years != null && Object.values(years).length > 1"
            v-model="activeYear"
            class="studyplan__header__select form-control"
          >
            <option
              v-for="year in years"
              :key="year"
              :value="year"
            >
              {{ year }} - {{ parseInt(year) + 1 }}
            </option>
          </select>
          <template v-if="!isApproved && canApprove">
            <button
              class="btn btn-default"
              :disabled="isChangingApproval"
              @click="approveStudyPlan"
            >
              {{ trans('studyplan.approve') }}
            </button>
          </template>
          <template v-else-if="isApproved">
            <div
              class="studyplan__header__approved"
            >
              <span class="fa fa-check-circle-o" /> {{ trans('studyplan.approved') }}
            </div>
          </template>
          <template v-else>
            <div
              class="studyplan__header__not-approved"
            >
              <span class="fa fa-check-circle-o" /> {{ trans('studyplan.unapproved') }}
            </div>
          </template>
        </div>
        <div class="studyplan__header__info">
          <i
            class=" fa fa-info-circle"
            :class="{ 'studyplan__header__info__button-visible':!isApproved && canApprove }"
            @click="showApprovalHistory = true"
          />
          <i
            class="fa fa-print studyplan__header__print"
            @click="printPage"
          />
        </div>
      </div>
      <div
        class="studyplan__header__open"
      >
        <a
          class="studyplan__header__open__link"
          @click="openAllCourses = !openAllCourses"
        > <span
            v-if="!openAllCourses"
            class="studyplan__header__open__text"
          >
            {{ trans('studyplan.open_all') }}
          </span>
          <span
            v-else
            class="studyplan__header__open__text"
          >
            {{ trans('studyplan.close_all') }}
          </span>
        </a>
      </div>

      <StudyPlan
        :subscribable-groupings="subscribableGroupings"
        :subscribables="subscribables"
        :courses="courses"
        :my-subscriptions="mySubscriptions"
        :all-courses="allCourses"
        :open-all-courses="openAllCourses"
      >
        <template
          slot="unsubscription"
          slot-scope="props"
        >
          <span
            class="fa fa-trash course__remove"
            @click="openUnsubscriptionModal(props.unsubscriptionCourseName, props.unsubscriptionSubscribableId)"
          />
        </template>
      </StudyPlan>

      <Modal
        v-if="showApprovalHistory"
        :open="showApprovalHistory"
        :title="trans('studyplan.approve_history')"
        @close="showApprovalHistory = false"
      >
        <ApprovalHistory
          :student-characteristics="student_characteristics"
          :users="users"
        />
        <template v-if="isApproved && canApprove">
          <button
            class="btn btn-default"
            :disabled="isChangingApproval"
            @click="unApproveStudyPlan"
          >
            {{ trans('studyplan.unapprove') }}
          </button>
        </template>
      </Modal>

      <Modal
        v-if="showUnsubscriptionModal && unsubscriptionCourseName != null && unsubscriptionSubscribableId != null"
        :open="showUnsubscriptionModal"
        :title="trans('subscription.unsubscribe') + ': ' + unsubscriptionCourseName"
        @close="showUnsubscriptionModal = false"
      >
        {{ trans('studyplan.unsubscribe_student_question', { 'course': unsubscriptionCourseName }) }}
        <div class="pull-right">
          <button
            class="btn btn-default"
            @click="showUnsubscriptionModal = false"
          >
            {{ trans('core.cancel') }}
          </button>
          <button
            class="btn btn-danger"
            :disabled="isChangingSubscription"
            @click="unsubscribeFromCourse"
          >
            {{ trans('subscription.unsubscribe') }}
          </button>
        </div>
        <br style="clear:both;">
      </Modal>
    </template>
  </div>
</template>

<script>

import { mapState } from 'vuex'
import StudyPlan from '@/shared/studyplan/StudyPlan'
import Modal from '@/employees/AttendanceList/components/Modal'
import toast from '@/services/toast'
import ApprovalHistory from '@/shared/studyplan/ApprovalHistory'
import { defaultErrorMessage, displayFloatLocalized } from '@/helpers'
import { uniqBy } from 'lodash'

export default {
  name: 'StudyPlanEmployee',
  components: { ApprovalHistory, Modal, StudyPlan },
  props: {
    studentId: {
      required: true,
    },
    selectedYear: {
      required: true,
    },
    years: {
      required: true,
    },
    canApprove: {
      required: true,
    },
  },
  data () {
    return {
      isLoading: true,
      showApprovalHistory: false,
      isChangingApproval: false,
      isChangingSubscription: false,
      openAllCourses: false,
      showUnsubscriptionModal: false,
      unsubscriptionCourseName: null,
      unsubscriptionSubscribableId: null,
      activeYear: this.selectedYear,
    }
  },
  computed: {
    ...mapState({
      subscribableGroupings (state) {
        return state.subscribableGroupings.subscribableGroupings
      },
      courses (state) {
        return state.courses.courses
      },
      subscribables (state) {
        return state.subscribables.subscribables
      },
      student_characteristics (state) {
        return state.studyplan.studentCharacteristics
      },
      users (state) {
        return state.users.users
      },
      mySubscriptions (state) {
        return state.studyplan.mySubscriptions
      },
      allCourses () {
        return Object.values(this.$store.state.courses.courses)
      },
      subscribedSubscribableGroupings () {
        return Object.values(this.subscribableGroupings).flatMap((grouping) => {
          // We filter to get the subscribed subscribables
          return grouping.subscribables.filter((id) => {
            return this.subscribables[id].mySubscription != null && this.mySubscriptions[this.subscribables[id].mySubscription].subscribed == 1
          }).map(id => this.courses[this.subscribables[id].course])
        })
      },
      getEcts () {
        return displayFloatLocalized(_.uniqBy(this.subscribedSubscribableGroupings, 'id').reduce((acc, course) => {
          acc += course.ects == null ? 0.0 : parseFloat(course.ects)
          return acc
        }, 0.0), Lang.locale)
      },
    }),
    isApproved () {
      return Object.values(this.student_characteristics).some((item) => {
        return item.value === '1' && item.end == null
      })
    },
  },
  watch: {
    activeYear (oldvalue, newvalue) {
      if (oldvalue !== newvalue) {
        this.isLoading = true
        this.$store.dispatch('resetSubscribableState')
        this.$store.dispatch('resetStudyPlanState')
        this.$store.dispatch('resetSubscribableGroupingState')
        this.loadData()
      }
    },
  },
  created () {
    this.loadData()
  },
  methods: {

    printPage () {
      return window.print()
    },
    loadData () {
      Promise.all([this.loadGroupings(), this.loadCharacteristics()])
        .finally(() => {
          this.isLoading = false
        })
    },
    loadGroupings () {
      return this.$store.dispatch('getStudyPlanSubscribableGroupings', {
        studentId: this.studentId,
        queryParams: {
          year: this.activeYear,
          includes: ['subscribables.course.myContexts', 'subscribables.mySubscription', 'subscribables.myAnswers'],
        },
      }).catch(error => defaultErrorMessage(error))
    },
    loadCharacteristics () {
      return this.$store.dispatch('getStudyPlanStudentCharacteristics', {
        studentId: this.studentId,
        queryParams: {
          includes: ['user'],
          year: this.activeYear,
        },
      }).catch((error) => {
        defaultErrorMessage(error)
        return error
      })
    },
    approveStudyPlan () {
      this.isChangingApproval = true

      return this.$store.dispatch('postStudyPlanApproved', {
        studentId: this.studentId,
        data: { year: this.activeYear },
      }, { errorHandle: false })
        .then(() => {
          toast.success(this.trans('studyplan.approved_successfully'))
          this.isChangingApproval = false
          return this.loadCharacteristics()
        }).catch(error => defaultErrorMessage(error))
    },
    unApproveStudyPlan () {
      this.isChangingApproval = true

      return this.$store.dispatch('postStudyPlanUnApproved', {
        studentId: this.studentId,
        data: { year: this.activeYear },
      }, { errorHandle: false })
        .then(() => {
          toast.success(this.trans('studyplan.unapproved_successfully'))
          this.loadCharacteristics()
          this.showApprovalHistory = false
          this.isChangingApproval = false
        }).catch(error => defaultErrorMessage(error))
    },

    openUnsubscriptionModal (courseName, subscribableId) {
      this.unsubscriptionCourseName = courseName
      this.unsubscriptionSubscribableId = subscribableId
      this.showUnsubscriptionModal = true
    },

    unsubscribeFromCourse () {
      this.isChangingSubscription = true

      return this.$store.dispatch('postUnsubscriptionCourse', {
        studentId: this.studentId,
        data: { subscribableId: this.unsubscriptionSubscribableId },
      }, { errorHandle: false })
        .then(() => {
          toast.success(this.trans('studyplan.unsubscribe_student_successfully', { 'course': this.unsubscriptionCourseName }))

          // reload groupings
          this.loadGroupings().finally(() => {
            this.showUnsubscriptionModal = false
            this.isChangingSubscription = false
          })
        }).catch(error => defaultErrorMessage(error))
    },
  },
}
</script>

<style scoped lang="scss">

    .course__remove {
        position: absolute;
        bottom: 12px;
        right: 14px;
        color: #b71c1c;
        cursor: pointer;
    }

    .studyplan {

        &__header {

            height: 50px;
            display: flex;

            &__print {
                padding-left: 5px;
            }

            &__info {
                flex: 0 0 40px;
                color: black;
                cursor: pointer;
                margin-top: 5px;
                margin-left: 5px;

                &__button-visible {
                    font-size: 15px;
                    margin-top: 10px;
                }
            }
            &__select{
              margin-top:10px;
              margin-right: 10px;
              margin-bottom:10px;
              padding-top:8px;
              padding-bottom:8px;
              min-width: 135px;
            }
            &__title {
                flex: 1 1 auto;

                h3 {
                    margin-top: 10px;

                    @media (max-width: 531px) {
                        font-size: 21px;
                    }
                    @media (max-width: 440px) {
                        font-size: 18px;
                    }
                    @media (max-width: 314px) {
                        font-size: 15px;
                    }
                }
            }

            &__open {
                font-size: 15px;
                font-style: italic;
                border-bottom: 1px solid #eeeeee;
                padding-bottom: 7px;
                margin-bottom: 10px;

                &__link {
                    cursor: pointer;
                }

                &__text {
                    color: black;
                }
            }

            &__approve {
                flex: 0 0 235px;
                text-align: right;
                margin-top: -5px;
                display: inherit;
                justify-content: flex-end;
            }

            &__approved {
                flex: 0 0 150px;
                color: green;
                text-align: right;
                font-size: 15px;
                margin-top: 10px;
            }

            &__not-approved {
                flex: 0 0 150px;
                color: green;
                text-align: right;
                font-size: 15px;
                margin-top: 10px;
            }
        }

        &__groupings {
            display: flex;
            overflow: auto;
            flex-direction: horizontal;

            &__item {
                min-width: 300px;
                border: 1px solid #eeeeee;
                height: inherit;

            }
        }

    }

</style>
